import type { ReactNode } from "react";
import { CircleAlertIcon, InfoIcon, TriangleAlertIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Tone } from "./format";
import type { AlertLevel } from "./types";

/** Text with an explanation on hover; the dotted underline says there is one. */
export function Hint({ text, children, underline = true }: { text: string; children: ReactNode; underline?: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className={cn(
            "cursor-help outline-none",
            underline && "underline decoration-muted-foreground/40 decoration-dotted underline-offset-[3px]"
          )}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-64 text-balance">{text}</p>
      </TooltipContent>
    </Tooltip>
  );
}

const TONE_BADGE: Record<Tone, "success" | "warning" | "error" | "ghost"> = {
  success: "success",
  warning: "warning",
  error: "error",
  muted: "ghost",
};

export function ToneBadge({ tone, children, className }: { tone: Tone; children: ReactNode; className?: string }) {
  return (
    <Badge variant={TONE_BADGE[tone]} className={className}>
      {children}
    </Badge>
  );
}

export function SeverityIcon({ level, className }: { level: AlertLevel; className?: string }) {
  const Icon = level === "critical" ? CircleAlertIcon : level === "warn" ? TriangleAlertIcon : InfoIcon;
  return (
    <Icon
      aria-label={level}
      className={cn(
        "size-4 shrink-0",
        level === "critical" ? "text-destructive" : level === "warn" ? "text-warning" : "text-muted-foreground",
        className
      )}
    />
  );
}
